import React from 'react';

/**
 * Centralized chart theme for Recharts components.
 * Colors are aligned with tailwind.config.js theme tokens.
 *
 * For dark/light switching use Tailwind classes on SVG elements:
 *   <CartesianGrid className="stroke-gray-200 dark:stroke-gray-700" />
 *   <XAxis className="fill-gray-500 dark:fill-gray-400" tick={{ fontSize: 11 }} />
 *   <YAxis className="fill-gray-500 dark:fill-gray-400" />
 *   <Tooltip content={<ChartTooltip />} />
 *   <Legend content={<ChartLegend />} />
 */

// Semantic colors for talent types
export const TYPE_COLORS = {
  candidate: '#3B82F6', // blue-500
  employee: '#10B981',  // emerald-500
  presale: '#F59E0B'    // amber-500
};

export const TYPE_LABELS = {
  candidate: 'Candidates',
  employee: 'Employees',
  presale: 'Presales'
};

/**
 * Subtle hover background for BarChart / AreaChart.
 * Pass as: <Tooltip cursor={CHART_CURSOR} content={...} />
 * Works in both light and dark themes (semi-transparent gray).
 */
export const CHART_CURSOR = { fill: 'rgba(156, 163, 175, 0.1)', strokeWidth: 0 };

/**
 * Active (hovered) bar style.
 * Pass as: <Bar activeBar={ACTIVE_BAR} ... />
 */
export const ACTIVE_BAR = {
  fillOpacity: 0.82,
  stroke: 'rgba(255, 255, 255, 0.45)',
  strokeWidth: 1.5
};

/**
 * Returns activeDot style for <Line> — white ring with colored border.
 * Pass as: <Line activeDot={activeDot(stroke)} ... />
 */
export const activeDot = (color) => ({
  r: 6,
  fill: '#ffffff',
  stroke: color,
  strokeWidth: 2.5
});

// General palette for pie charts, multi-series, etc.
export const CHART_COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#8B5CF6', // violet-500
  '#EF4444', // red-500
  '#EC4899', // pink-500
  '#14B8A6', // teal-500
  '#F97316', // orange-500
  '#6366F1', // indigo-500
  '#84CC16'  // lime-500
];

/**
 * Custom Tooltip component for Recharts.
 * Uses Tailwind classes so it auto-switches with dark mode.
 */
export const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-lg text-sm">
      {label != null && (
        <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{label}</p>
      )}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <span
            className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span>{entry.name}: <span className="font-medium">{entry.value}</span></span>
        </div>
      ))}
    </div>
  );
};

/**
 * Custom Legend component for Recharts.
 * Uses Tailwind classes so it auto-switches with dark mode.
 */
export const ChartLegend = ({ payload }) => {
  if (!payload?.length) return null;

  return (
    <div className="flex flex-wrap justify-center gap-4 mt-2 text-sm">
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <span
            className="w-3 h-3 rounded-sm inline-block"
            style={{ backgroundColor: entry.color }}
          />
          <span>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};
